import { defineStore } from "pinia";

export interface WishlistItem {
  productId: string;
  name: string;
  price: number;
  image?: string;
  affiliateUrl?: string;
  merchantName?: string;
  ean?: string;
  addedAt: string;
}

export const useWishlistStore = defineStore("wishlist", () => {
  const items = ref<WishlistItem[]>([]);
  const loading = ref(false);
  const synced = ref(false);

  const totalItems = computed(() => items.value.length);

  const isInWishlist = (productId: string) =>
    items.value.some((i) => i.productId === productId);

  const isAuthenticated = () => {
    if (typeof window === 'undefined') return false;
    return !!localStorage.getItem("auth_token");
  };

  const toggleItem = async (product: Omit<WishlistItem, "addedAt">) => {
    if (isInWishlist(product.productId)) {
      await removeItem(product.productId);
    } else {
      await addItem(product);
    }
  };

  const addItem = async (product: Omit<WishlistItem, "addedAt">) => {
    if (isInWishlist(product.productId)) return;
    items.value.push({ ...product, addedAt: new Date().toISOString() });
    saveToStorage();
    if (isAuthenticated()) {
      try {
        const api = useApi();
        await api("/api/wishlist/toggle", {
          method: "POST",
          body: { productId: product.productId },
        });
      } catch (err) {
        console.warn("Wishlist API add failed, kept in localStorage:", err);
      }
    }
  };

  const removeItem = async (productId: string) => {
    items.value = items.value.filter((i) => i.productId !== productId);
    saveToStorage();
    if (isAuthenticated()) {
      try {
        const api = useApi();
        await api("/api/wishlist/toggle", {
          method: "POST",
          body: { productId },
        });
      } catch (err) {
        console.warn("Wishlist API remove failed:", err);
      }
    }
  };

  const clearWishlist = async () => {
    const oldItems = [...items.value];
    items.value = [];
    saveToStorage();
    if (isAuthenticated()) {
      const api = useApi();
      for (const item of oldItems) {
        try {
          await api("/api/wishlist/toggle", {
            method: "POST",
            body: { productId: item.productId },
          });
        } catch {}
      }
    }
  };

  const syncWithServer = async () => {
    if (!isAuthenticated() || synced.value) return;
    loading.value = true;
    try {
      const api = useApi();
      const localIds = items.value.map((i) => i.productId);
      await api<any>("/api/wishlist/sync", {
        method: "POST",
        body: { productIds: localIds },
      });
      // Re-fetch merged list from server
      await fetchFromServer();
      synced.value = true;
    } catch (err) {
      console.warn("Wishlist sync failed, using localStorage:", err);
    } finally {
      loading.value = false;
    }
  };

  const fetchFromServer = async () => {
    if (!isAuthenticated()) return;
    try {
      const api = useApi();
      const response = await api<{ items: any[] }>("/api/wishlist");
      if (response.items) {
        items.value = response.items.map((wi: any) => ({
          productId: wi.product?.id ?? wi.productId,
          name: wi.product?.name ?? wi.name ?? '',
          price: wi.product?.price ?? wi.price ?? 0,
          image: wi.product?.image ?? wi.image ?? null,
          affiliateUrl: wi.product?.affiliateUrl ?? wi.affiliateUrl ?? '',
          merchantName: wi.product?.merchantName ?? wi.merchantName ?? '',
          ean: wi.product?.ean ?? wi.ean ?? null,
          addedAt: wi.addedAt,
        }));
        saveToStorage();
        synced.value = true;
      }
    } catch (err) {
      console.warn("Wishlist fetch failed, using localStorage:", err);
    }
  };

  const saveToStorage = () => {
    if (typeof window !== 'undefined') {
      localStorage.setItem("wishlist", JSON.stringify(items.value));
    }
  };

  const loadFromStorage = () => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem("wishlist");
      if (saved) {
        try {
          items.value = JSON.parse(saved);
        } catch {}
      }
    }
  };

  const resetSyncState = () => {
    synced.value = false;
  };

  loadFromStorage();

  return {
    items,
    totalItems,
    loading,
    synced,
    isInWishlist,
    toggleItem,
    addItem,
    removeItem,
    clearWishlist,
    syncWithServer,
    fetchFromServer,
    loadFromStorage,
    resetSyncState,
  };
});
